import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  deleteUser,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { useAuth } from '../hooks/useAuth';

export function LoginPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate('/dashboard');
  }, [user, loading, navigate]);

  const handleGoogle = async () => {
    setError('');
    setSubmitting(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      // If this Google account has never signed in before, it's not pre-approved
      const isNew = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
      if (isNew) {
        await deleteUser(result.user);
        setError('Access denied. Contact your administrator to be added.');
        return;
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Sign-in failed';
      setError(msg.replace('Firebase: ', '').replace(/ \(auth\/.*\)/, ''));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e: unknown) {
      const code = (e as { code?: string }).code;
      if (code === 'auth/user-not-found' || code === 'auth/invalid-credential') {
        setError('Access denied. Contact your administrator to be added.');
      } else if (code === 'auth/wrong-password') {
        setError('Incorrect password.');
      } else {
        const msg = e instanceof Error ? e.message : 'Sign-in failed';
        setError(msg.replace('Firebase: ', '').replace(/ \(auth\/.*\)/, ''));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-falcon-navy flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <p className="font-display font-bold text-4xl text-white tracking-tight">
            Falcon <span className="text-falcon-gold">MOS</span>
          </p>
          <p className="text-sm text-gray-400 font-body mt-2">
            Marketing Operations System
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Google button */}
          <button
            onClick={handleGoogle}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 font-body hover:bg-gray-50 transition-colors disabled:opacity-50 mb-6"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-100" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs text-gray-400 font-body">or</span>
            </div>
          </div>

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 font-body mb-1.5">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@falconincentives.com"
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl font-body focus:outline-none focus:ring-2 focus:ring-falcon-blue focus:border-transparent placeholder-gray-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 font-body mb-1.5">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl font-body focus:outline-none focus:ring-2 focus:ring-falcon-blue focus:border-transparent placeholder-gray-300"
              />
            </div>

            {error && (
              <p className="text-xs text-red-600 font-body bg-red-50 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-falcon-navy text-white text-sm font-medium font-body rounded-xl hover:bg-falcon-blue transition-colors disabled:opacity-50"
            >
              {submitting ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6 font-body">
          Access is by invitation only
        </p>
      </div>
    </div>
  );
}
